import axios from 'axios';
import config from '../config';

const api = axios.create({
  baseURL: config.apiBaseUrl,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const requestUrl = String(err.config?.url || '');
    const isAuthEntry = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');
    if (err.response?.status === 401 && !isAuthEntry) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = config.redirectOnUnauthorized;
    }
    return Promise.reject(err);
  }
);

export const auth = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  register: (data) => api.post('/auth/register', data),
  getReferrer: (username) => api.get(`/auth/referrer/${encodeURIComponent(username)}`),
  me: () => api.get('/auth/me')
};

export const users = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id, transferTo) => api.delete(`/users/${id}`, { data: { transferTo } }),
  disable: (id) => api.post(`/users/${id}/disable`),
  resetPassword: (id, password) => api.post(`/users/${id}/reset-password`, { password }),
  resetPasswordOnce: (id) => api.post(`/users/${id}/reset-password-once`),
  changePassword: (currentPassword, newPassword) => api.post('/users/change-password', { currentPassword, newPassword }),
  getActivity: (id, params) => api.get(`/users/${id}/activity`, { params }),
  getOrders: (id, params) => api.get(`/users/${id}/orders`, { params }),
  getDownlines: (id) => api.get(`/users/${id}/downlines`),
  getGcash: (id) => api.get(`/users/${id}/gcash`),
  updateGcash: (id, data) => api.put(`/users/${id}/gcash`, data),
  getHistory: (id) => api.get(`/users/${id}/history`),
  submitParentChangeRequest: (data) => api.post('/users/parent-change-request', data),
  getParentChangeRequests: () => api.get('/users/admin/parent-change-requests'),
  reviewParentChangeRequest: (id, action) => api.put(`/users/admin/parent-change-requests/${id}`, { action })
};

export const auditLogs = {
  list: (params) => api.get('/audit-logs', { params }),
  actions: () => api.get('/audit-logs/actions'),
  entityTypes: () => api.get('/audit-logs/entity-types')
};

export const earnings = {
  get: (params) => api.get('/earnings', { params }),
  summary: () => api.get('/earnings/summary')
};

export const network = {
  summary: (params) => api.get('/network/summary', { params }),
  tree: () => api.get('/network/tree'),
  getUpline: () => api.get('/network/upline'),
  getUser: (userId) => api.get(`/network/${userId}`),
  manage: (userId, data) => api.put(`/network/${userId}/manage`, data)
};

export const statements = {
  list: (params) => api.get('/statements', { params }),
  get: (id) => api.get(`/statements/${id}`),
  searchUsers: (keyword) => api.get('/statements/search-users', { params: { keyword } }),
  listUnsettledEarnings: (params) => api.get('/statements/unsettled-earnings', { params }),
  settle: (data) => api.post('/statements/settle', data),
  generate: (data) => api.post('/statements/generate', data),
  // Status changes
  confirm: (id) => api.post(`/statements/${id}/confirm`),
  submitAudit: (id) => api.post(`/statements/${id}/submit-audit`),
  approvePayment: (id) => api.post(`/statements/${id}/approve-payment`),
  markPaid: (id) => api.post(`/statements/${id}/mark-paid`),
  reset: (id) => api.post(`/statements/${id}/reset`),
  // Adjustments
  getAdjustments: (id) => api.get(`/statements/${id}/adjustments`),
  addAdjustment: (id, data) => api.post(`/statements/${id}/adjustments`, data),
  updateAdjustment: (id, adjId, data) => api.put(`/statements/${id}/adjustments/${adjId}`, data),
  deleteAdjustment: (id, adjId) => api.delete(`/statements/${id}/adjustments/${adjId}`),
  // Legacy
  audit: (id) => api.post(`/statements/${id}/audit`),
  paid: (id) => api.post(`/statements/${id}/paid`),
  void: (id) => api.post(`/statements/${id}/void`)
};

export const demo = {
  reset: () => api.post('/demo/reset'),
  status: () => api.get('/demo/status'),
  scenario: (scenario) => api.post('/demo/scenario', { scenario }),
  monthlyClose: (period) => api.post('/demo/monthly-close', { period })
};

export const allocationRules = {
  list: () => api.get('/allocation-rules'),
  create: (data) => api.post('/allocation-rules', data),
  update: (id, data) => api.put(`/allocation-rules/${id}`, data)
};

export const customers = {
  list: (params) => api.get('/customers', { params }),
  adminSearch: (params) => api.get('/customers/admin/search', { params }),
  getProfile: (mobile) => api.get(`/customers/${mobile}/profile`),
  getPreapprovals: (mobile) => api.get(`/customers/${mobile}/preapprovals`),
  getOrders: (mobile) => api.get(`/customers/${mobile}/orders`),
  testConnection: () => api.get('/customers/test-connection')
};

export const inviteCodes = {
  list: () => api.get('/invite-codes'),
  getByPromoter: (promoterId) => api.get(`/invite-codes/promoter/${promoterId}`),
  create: (data) => api.post('/invite-codes', data),
  update: (id, data) => api.put(`/invite-codes/${id}`, data),
  delete: (id) => api.delete(`/invite-codes/${id}`),
  getAvailable: () => api.get('/invite-codes/available'),
  getMyCode: () => api.get('/invite-codes/my-code')
};

export const notifications = {
  subscribe: (payload) => api.post('/notifications/subscribe', payload)
};

export const broadcast = {
  get: () => api.get('/broadcast'),
  update: (data) => api.put('/broadcast', data)
};

export const dashboard = {
  progressStats: (params) => api.get('/dashboard/progress-stats', { params })
};

export default api;
